/**
 * NotificationCenter Component
 * Displays notifications dropdown with mark as read and delete functionality
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  getNotifications,
  markNotificationAsRead,
  markAllAsRead,
  deleteNotification,
  type Notification,
} from '../../../services/notificationsApi';
import './NotificationCenter.css';

interface NotificationCenterProps {
  onNotificationClick?: (notification: Notification) => void;
}

export const NotificationCenter: React.FC<NotificationCenterProps> = ({
  onNotificationClick,
}) => {
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchNotifications = useCallback(async () => {
    // Double-check authentication before making API call
    if (!isAuthenticated) {
      return;
    }

    setLoading(true);
    try {
      const result = await getNotifications({ limit: 10 });
      if (result.success && result.data) {
        // API returns: { success: true, data: [...], pagination: {...} }
        // Handle both array response and nested object response
        const notifications = Array.isArray(result.data)
          ? result.data  // Direct array
          : (result.data as any).data || [];  // Nested in data.data

        const pagination = (result as any).pagination || (result.data as any).pagination || {};

        setNotifications(notifications);
        setUnreadCount(pagination.unreadCount || 0);
      } else if (result.error) {
        // Handle API errors gracefully (404, 500, etc.)
        // If notifications endpoint is not implemented yet, just show empty state
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (error) {
      // Silently handle network errors or 404s
      // Don't spam console with expected errors during development
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isOpen && isAuthenticated) {
      fetchNotifications();
    }
  }, [isOpen, isAuthenticated, fetchNotifications]);

  // Fetch notifications on mount for badge count (when authenticated)
  useEffect(() => {
    if (isAuthenticated && !isOpen) {
      // Only fetch if we haven't already fetched due to dropdown being open
      fetchNotifications();
    }
  }, [isAuthenticated, isOpen, fetchNotifications]);

  // Poll for new notifications every 3 minutes (only if authenticated)
  useEffect(() => {
    if (!isAuthenticated) {
      return; // Don't poll if user is not authenticated
    }

    const pollInterval = setInterval(() => {
      fetchNotifications();
    }, 180000); // 3 minutes

    return () => clearInterval(pollInterval);
  }, [fetchNotifications, isAuthenticated]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAsRead = async (
    e: React.MouseEvent,
    notificationId: string
  ) => {
    e.stopPropagation();
    try {
      await markNotificationAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === notificationId ? { ...notif, isRead: true } : notif
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      setNotifications((prev) => prev.map((notif) => ({ ...notif, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleDelete = async (
    e: React.MouseEvent,
    notificationId: string
  ) => {
    e.stopPropagation();
    try {
      await deleteNotification(notificationId);
      setNotifications((prev) => prev.filter((notif) => notif.id !== notificationId));
      const deletedNotification = notifications.find((n) => n.id === notificationId);
      if (deletedNotification && !deletedNotification.isRead) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markNotificationAsRead(notification.id);
      setNotifications((prev) =>
        prev.map((notif) =>
          notif.id === notification.id ? { ...notif, isRead: true } : notif
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
    setIsOpen(false);
    onNotificationClick?.(notification);
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'wish_contacted':
        return '💬';
      case 'new_ad_match':
        return '🎯';
      case 'system_announcement':
        return '📢';
      default:
        return '🔔';
    }
  };

  return (
    <div className="notification-center" ref={dropdownRef}>
      <button
        className="notification-center__button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label="Notifications"
      >
        <svg
          className="notification-center__icon"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="notification-center__badge">{unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notification-center__dropdown">
          <div className="notification-center__header">
            <h3>通知</h3>
            {unreadCount > 0 && (
              <button
                className="notification-center__mark-all"
                onClick={handleMarkAllAsRead}
              >
                全部標記已讀
              </button>
            )}
          </div>

          <div className="notification-center__content">
            {loading ? (
              <div className="notification-center__loading">載入中...</div>
            ) : notifications.length === 0 ? (
              <div className="notification-center__empty">沒有通知</div>
            ) : (
              <ul className="notification-center__list">
                {notifications.map((notification) => (
                  <li
                    key={notification.id}
                    className={`notification-center__item ${
                      !notification.isRead ? 'notification-center__item--unread' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-center__item-icon">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="notification-center__item-content">
                      <div className="notification-center__item-title">
                        {notification.title}
                      </div>
                      {notification.message && (
                        <div className="notification-center__item-message">
                          {notification.message}
                        </div>
                      )}
                      <div className="notification-center__item-time">
                        {new Date(notification.createdAt).toLocaleString('zh-TW')}
                      </div>
                    </div>
                    <div className="notification-center__item-actions">
                      {!notification.isRead && (
                        <button
                          className="notification-center__action"
                          onClick={(e) => handleMarkAsRead(e, notification.id)}
                          aria-label="Mark as read"
                        >
                          ✓
                        </button>
                      )}
                      <button
                        className="notification-center__action"
                        onClick={(e) => handleDelete(e, notification.id)}
                        aria-label="Delete notification"
                      >
                        ×
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
